import React from 'react';
import styled from 'styled-components';
import CopyIcon from '../../../assets/icons/CopyIcon';
import { OrganizationInfo } from '../../../types/types';
import Avatar from '../../../components/Avatar/Avatar';
import StyledText from '../../../components/StyledComponents/StyledText';
import { CorporateTheme, font, Theme } from '../../../utils/themes';
import { hexToRgba } from '../../../utils/helper';
import NewCopyIcon from '../../../assets/icons/NewCopyIcon';
import TickIcon from '../../../assets/icons/TickIcon';

const ProfileContainer = styled.div`
  display: flex;
  gap: 24px;
  width: calc(100% - 306px);

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const LogoContainer = styled.div`
  flex-shrink: 0;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 4px solid #f9fafb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const LogoPlaceholder = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: bold;
  text-align: center;
  padding: 8px;
`;

const DetailsGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Name = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const Text = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const IdBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  background-color: ${hexToRgba(CorporateTheme.colors.blueberry, 0.05)};
  border: 1px solid ${CorporateTheme.colors.blueberry};
  border-radius: 4px;
  width: fit-content;
`;

const IdText = styled.span`
  font-size: 13px;
  color: #374151;
  font-weight: 500;
`;

const Roles = styled.div<{ theme: Theme }>`
 display: flex;
 align-items: center;
 justify-content: center;
 border: 1px solid #EBEAEB;
   background-color: ${hexToRgba(CorporateTheme.colors.blueberry, 0.05)};
  padding: 7px 12px;
  border-radius: 50px;
`;


interface ProfileProps {
  data: OrganizationInfo;
  showWebsite?: boolean;
  idLabel?: string;
}

export const OrganizationProfile: React.FC<ProfileProps> = ({ data, showWebsite, idLabel = 'Organization ID' }) => {
  const [isCopied, setIsCopied] = React.useState(false);
  const copyResetTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyId = () => {
    navigator.clipboard.writeText(data.organizationId);
    setIsCopied(true);

    if (copyResetTimeoutRef.current) {
      clearTimeout(copyResetTimeoutRef.current);
    }

    copyResetTimeoutRef.current = setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  return (
    <ProfileContainer>
      {/* <LogoContainer>
        {data.logoUrl ? (
          <img src={data.logoUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <LogoPlaceholder>NV Seal</LogoPlaceholder>
        )}
      </LogoContainer> */}
      <Avatar name={data.name} imageUrl={data.logoUrl} size={120} border="4px solid #FFFFFF" />

      <DetailsGroup>
        <StyledText fontSize='18px' fontWeight={700} textColor={'var(--app-primary-color)'}>{data.name}</StyledText>
        <StyledText fontSize='16px' fontWeight={400} textColor={'var(--app-text-color)'}>{data.email}</StyledText>
        {showWebsite && (
          <StyledText fontSize='16px' fontWeight={400} textColor={'var(--app-text-color)'}>
            {data.website}
          </StyledText>
        )}

        <IdBadge>
          <div className='d-flex align-items-center gap-2'>
            <StyledText fontSize='12px' fontWeight={600} textColor={'var(--app-text-color)'}>{idLabel}:</StyledText>
            <StyledText fontSize='12px' fontWeight={500} textColor={'var(--app-text-color)'} fontFamily={font.lato}>{data.organizationId}</StyledText>
          </div>

          <div onClick={handleCopyId} aria-label="Copy ID" className='d-flex align-items-center justify-content-center' style={{ cursor: 'pointer' }}>
            {isCopied ? (
              <TickIcon size={14} color='var(--app-secondary-color)' />
            ) : (
              <NewCopyIcon size={14} color='var(--app-secondary-color)' />
            )}
          </div>
        </IdBadge>

        <div className="d-flex mt-2 gap-2 flex-wrap">
          {data?.roles?.map((role, index) => (
            <Roles
              key={index}
            >
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  display: 'block',
                  borderRadius: '50%',
                  marginRight: '5px',
                  backgroundColor: '#009828',
                }}
              ></span>
              <StyledText
                fontSize="14px"
                fontWeight={500}
                textColor={'var(--app-text-color)'}
              >
                {role}
              </StyledText>
            </Roles>
          ))}
        </div>
      </DetailsGroup>
    </ProfileContainer>
  );
};


